<template>
  <main class="mx-auto max-w-3xl px-4 py-12">
    <h1 class="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">Create Your Story</h1>
    <p class="mt-2 text-zinc-500 dark:text-zinc-400">Follow the steps to generate a unique AI-powered tale.</p>

    <!-- Step indicator -->
    <div class="mt-8 flex items-center gap-3">
      <button
        v-for="s in 3"
        :key="s"
        @click="goToStep(s)"
        :disabled="s > maxReachedStep"
        class="flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition"
        :class="
          s === store.step
            ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/30'
            : s <= maxReachedStep
              ? 'bg-zinc-100 text-zinc-500 hover:text-zinc-900 dark:bg-surface-dark dark:text-zinc-400 dark:hover:text-zinc-50'
              : 'bg-zinc-50 text-zinc-300 cursor-not-allowed dark:bg-surface-dark/50 dark:text-zinc-600'
        "
      >
        {{ s }}
      </button>
      <div class="ml-2 text-sm text-zinc-400 dark:text-zinc-500">
        {{ stepLabels[store.step - 1] }}
      </div>
    </div>

    <!-- Step panels -->
    <div class="mt-8">
      <WizardStepOptions v-if="store.step === 1" @next="nextStep" />
      <WizardStepPrompt v-else-if="store.step === 2" @back="prevStep" @next="nextStep" />
      <WizardStepResult v-else @back="prevStep" @new-story="handleNewStory" />
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useStoryStore } from '@/stores/story';
import WizardStepOptions from '@/components/StoryWizard/WizardStepOptions.vue';
import WizardStepPrompt from '@/components/StoryWizard/WizardStepPrompt.vue';
import WizardStepResult from '@/components/StoryWizard/WizardStepResult.vue';

const store = useStoryStore();
const maxReachedStep = ref(store.step);

const stepLabels = ['Choose Options', 'Review Prompt', 'Your Story'];

function goToStep(s) {
  if (s <= maxReachedStep.value) store.step = s;
}

function nextStep() {
  store.step = Math.min(store.step + 1, 3);
  if (store.step > maxReachedStep.value) maxReachedStep.value = store.step;
}

function prevStep() {
  store.step = Math.max(store.step - 1, 1);
}

function handleNewStory() {
  store.resetWizard();
  maxReachedStep.value = 1;
}

const resetOnLeave = computed(() => store.step);
onUnmounted(() => void resetOnLeave.value);
</script>
